// Lifted from schedugram-client/react-src/components/ui/data-table/data-table-row.tsx
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { Column } from "./types";

interface DataTableRowProps<T> {
  row: T;
  rowIndex: number;
  columns: Column<T>[];
  isLast: boolean;
  onRowClick?: (row: T) => void;
  rowClassName?: string | ((row: T) => string);
  showCellBorders?: boolean;
}

export function DataTableRow<T>({
  row,
  rowIndex,
  columns,
  isLast,
  onRowClick,
  rowClassName,
  showCellBorders = false,
}: DataTableRowProps<T>) {
  const computedRowClassName =
    typeof rowClassName === "function" ? rowClassName(row) : rowClassName;

  // Mouse + keyboard activation when the row is interactive. Using role=button
  // tells AT this row is a single action target; tabIndex=0 puts it in the tab
  // order; the keydown handler maps Enter and Space to the click handler.
  const interactive = Boolean(onRowClick);
  return (
    <tr
      className={cn(
        "bg-white transition-colors hover:bg-neutral-50",
        interactive &&
          "cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-purple-500",
        computedRowClassName,
      )}
      onClick={onRowClick ? () => onRowClick(row) : undefined}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onRowClick?.(row);
              }
            }
          : undefined
      }
    >
      {columns.map((column, colIndex) => (
        <DataTableCell
          key={column.id}
          row={row}
          rowIndex={rowIndex}
          column={column}
          isLastRow={isLast}
          isLastCol={colIndex === columns.length - 1}
          showCellBorders={showCellBorders}
        />
      ))}
    </tr>
  );
}

interface DataTableCellProps<T> {
  row: T;
  rowIndex: number;
  column: Column<T>;
  isLastRow: boolean;
  isLastCol: boolean;
  showCellBorders?: boolean;
}

function DataTableCell<T>({
  row,
  rowIndex,
  column,
  isLastRow,
  isLastCol,
  showCellBorders = false,
}: DataTableCellProps<T>) {
  let content: ReactNode;
  if (column.cell) {
    content = column.cell(row, rowIndex);
  } else if (column.accessorKey) {
    const value = row[column.accessorKey];
    content = value != null ? String(value) : "";
  } else {
    content = null;
  }

  return (
    <td
      className={cn(
        "px-4 py-4",
        !isLastRow && "border-b border-neutral-200",
        showCellBorders && !isLastCol && "border-r border-neutral-200",
        column.align === "center" && "text-center",
        column.align === "right" && "text-right",
        column.cellClassName,
      )}
      style={{
        width: column.width,
        minWidth: column.minWidth,
        maxWidth: column.maxWidth,
      }}
    >
      {content}
    </td>
  );
}
